import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import BackToTop from './components/BackToTop';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import AnalysisPage from './pages/AnalysisPage';
import TrendsPage from './pages/TrendsPage';
import ProfilePage from './pages/ProfilePage';
import SharedReportPage from './pages/SharedReportPage';
import FacilityReportPage from './pages/FacilityReportPage';
import PricingPage from './pages/PricingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AppointmentPrepPage from './pages/AppointmentPrepPage';
import MedicationCheckPage from './pages/MedicationCheckPage';
import CareGapsPage from './pages/CareGapsPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HelpPage from './pages/HelpPage';
import TutorialsPage from './pages/TutorialsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import WelcomeTour from './components/WelcomeTour';
import ReferralFlow from './components/ReferralFlow';

function LaunchPage() {
  React.useEffect(() => {
    window.location.replace('/launch.html');
  }, []);
  return null;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-slate-400">Loading…</div>;
  return user ? children : <Navigate to="/login" replace />;
}

// HIPAA §164.312(a)(2)(iii): Automatic logoff warning modal
function SessionTimeoutWarning() {
  const { sessionWarning, extendSession, logout } = useAuth();
  const navigate = useNavigate();
  if (!sessionWarning) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Session expiring soon</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              For your security, you'll be signed out in 1 minute due to inactivity.
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
          <button
            onClick={extendSession}
            className="flex-1 bg-sky-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-sky-700 transition-colors"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SessionTimeoutWarning />
      <WelcomeTour />
      <ReferralFlow variant="banner" />
      <BackToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/launch" element={<LaunchPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/share/:shareToken" element={<SharedReportPage />} />
        {/* Public: the token is the credential, because facilities have no accounts. */}
        <Route path="/facility-report/:token" element={<FacilityReportPage />} />
        <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="upload" element={<UploadPage />} />
          <Route path="analysis/:sessionId" element={<AnalysisPage />} />
          <Route path="trends" element={<TrendsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="appointment-prep" element={<AppointmentPrepPage />} />
          <Route path="medication-check" element={<MedicationCheckPage />} />
          <Route path="care-gaps" element={<CareGapsPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="tutorials" element={<TutorialsPage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}
